import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, ChevronRight, Truck, Store, Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import CategoryBar from "@/components/CategoryBar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Seo, { SITE_URL } from "@/components/Seo";
import { CartProvider, useCart } from "@/components/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const catSlug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const saleActive = (p) =>
  p.sale_price_cents > 0 && p.sale_price_cents < p.price_cents &&
  (!p.sale_ends_at || new Date(p.sale_ends_at) > new Date());

function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    axios.get(`${API}/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-extrabold uppercase">Treat not found</h1>
        <p className="mt-3 text-sm text-[#4A4A4A]">This product may have sold out for good.</p>
        <Link to="/" className="hard-shadow hard-shadow-hover mt-6 inline-block rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-6 py-3 font-display text-sm font-extrabold uppercase">
          Back to the store
        </Link>
      </div>
    );
  }
  if (!product) {
    return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#FF2A54]" /></div>;
  }

  const soldOut = product.stock === 0;
  const onSale = saleActive(product);
  const effective = onSale ? product.sale_price_cents : product.price_cents;
  const canonical = `${SITE_URL}/product/${product.slug}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: product.image,
      description: product.blurb,
      brand: { "@type": "Brand", name: "Canadian Beats Marketplace" },
      offers: {
        "@type": "Offer",
        url: canonical,
        priceCurrency: "CAD",
        price: (effective / 100).toFixed(2),
        availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        ...(onSale && product.sale_ends_at ? { priceValidUntil: product.sale_ends_at.slice(0, 10) } : {}),
        seller: { "@id": `${SITE_URL}/#localbusiness` },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: product.tag, item: `${SITE_URL}/category/${catSlug(product.tag)}` },
        { "@type": "ListItem", position: 3, name: product.name, item: canonical },
      ],
    },
  ];

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-14">
      <Seo
        title={`${product.name} | Canadian Beats Marketplace`}
        description={`${product.blurb} Buy ${product.name} online in Canada — ships across Canada from Meteghan River, Nova Scotia. Local pickup available.`}
        canonical={canonical}
        image={product.image}
        type="product"
        jsonLd={jsonLd}
      />
      <nav aria-label="Breadcrumb" data-testid="product-breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#4A4A4A]">
        <Link to="/" className="hover:text-[#FF2A54]">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/category/${catSlug(product.tag)}`} className="hover:text-[#FF2A54]">{product.tag}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[#0A0A0A]">{product.name}</span>
      </nav>

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div className="hard-shadow-lg overflow-hidden rounded-2xl border-2 border-[#0A0A0A] bg-[#FFD700]">
          <img src={product.image} alt={product.name} className="aspect-[4/5] h-full w-full object-cover" />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-wrap gap-2">
            <span className={`w-fit rounded-full border-2 border-[#0A0A0A] px-3 py-0.5 text-xs font-bold uppercase tracking-widest ${soldOut ? "bg-[#FF2A54] text-white" : "bg-[#FFD700]"}`}>
              {soldOut ? "Sold Out" : product.stock <= 3 ? `Only ${product.stock} left` : product.tag}
            </span>
            {onSale && !soldOut && (
              <span className="w-fit rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-3 py-0.5 text-xs font-black uppercase tracking-widest text-white">Sale</span>
            )}
          </div>
          <h1 data-testid="product-page-name" className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl">
            {product.name}
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-[#4A4A4A]">{product.blurb}</p>
          <p data-testid="product-page-price" className="mt-6 font-display text-3xl font-extrabold">
            {onSale ? (
              <>
                <span className="mr-2 text-lg font-bold text-[#4A4A4A] line-through">{fmt(product.price_cents)}</span>
                <span className="text-[#FF2A54]">{fmt(product.sale_price_cents)}</span>
              </>
            ) : (
              fmt(product.price_cents)
            )}
          </p>
          <button
            onClick={() => { add({ ...product, price_cents: effective }); toast.success(`${product.name} added to cart`); }}
            disabled={soldOut}
            data-testid="product-page-add-to-cart"
            className="hard-shadow-lg hard-shadow-hover mt-6 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full border-2 border-[#0A0A0A] bg-[#0A0A0A] px-8 py-4 font-display text-base font-black uppercase text-white transition-colors hover:bg-[#FF2A54] disabled:opacity-60"
          >
            <ShoppingBag className="h-5 w-5" /> {soldOut ? "Sold Out" : "Add to Cart"}
          </button>
          <ul className="mt-8 flex flex-col gap-2 border-t-2 border-dashed border-[#0A0A0A]/20 pt-6 text-sm font-medium text-[#4A4A4A]">
            <li className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#FF2A54]" /> Ships across Canada</li>
            <li className="flex items-center gap-2"><Store className="h-4 w-4 text-[#FF2A54]" /> Local pickup in Meteghan River, Nova Scotia</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function ProductPage() {
  return (
    <CartProvider>
      <main className="overflow-x-clip">
        <Nav />
        <CategoryBar />
        <ProductDetail />
        <Footer />
        <CartDrawer />
      </main>
    </CartProvider>
  );
}
